import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { PageHeader, Loading, Badge } from '../components/UI';
import { heatmapService, auditService } from '../services/api';

// ── Helpers ───────────────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—';

const ACTION_COLORS = {
  CREATE: '#22c55e', UPDATE: '#3b82f6', DELETE: '#ef4444',
  LOGIN: '#8b5cf6', EMIT: '#f59e0b', SIGN: '#06b6d4',
  CHECKIN: '#10b981', CHECKOUT: '#f97316', PAY: '#22c55e',
};

const ACTION_ICONS = {
  CREATE: '➕', UPDATE: '✏️', DELETE: '🗑️',
  LOGIN: '🔑', EMIT: '📄', SIGN: '✍️',
  CHECKIN: '✅', CHECKOUT: '🏁', PAY: '💰',
};

const TYPE_LABELS = {
  entrega: '🚛 Entrega', coleta: '📦 Coleta', limpeza: '🧹 Limpeza',
};

// ── Heatmap Page ──────────────────────────────────────────────────────
export function HeatmapPage() {
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('calendar'); // calendar | list | chart

  useEffect(() => {
    (async () => {
      try {
        const r = await heatmapService.getOperations();
        const ops = Array.isArray(r.data) ? r.data : r.data?.data ?? [];
        setOperations(ops);
      } catch { toast.error('Erro ao carregar operações'); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = operations.filter(op =>
    filter === 'all' || op.logistics_type === filter
  );

  // Group by date for calendar heatmap
  const byDate = {};
  filtered.forEach(op => {
    if (!op.scheduled_date) return;
    const date = op.scheduled_date.slice(0, 10);
    if (!byDate[date]) byDate[date] = { total: 0, entrega: 0, coleta: 0, limpeza: 0 };
    byDate[date].total++;
    if (op.logistics_type) byDate[date][op.logistics_type] = (byDate[date][op.logistics_type] || 0) + 1;
  });

  // Last 12 weeks for heatmap
  const weeks = [];
  const today = new Date();
  for (let w = 11; w >= 0; w--) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      const date = new Date(today);
      date.setDate(today.getDate() - (w * 7) - (6 - d));
      const key = date.toISOString().slice(0, 10);
      week.push({ date: key, label: date.toLocaleDateString('pt-BR'), data: byDate[key] || null });
    }
    weeks.push(week);
  }

  // Monthly chart data
  const byMonth = {};
  filtered.forEach(op => {
    if (!op.scheduled_date) return;
    const month = op.scheduled_date.slice(0, 7);
    byMonth[month] = (byMonth[month] || 0) + 1;
  });
  const monthData = Object.entries(byMonth).sort().slice(-6);

  const maxCount = Math.max(...Object.values(byDate).map(d => d.total), 1);
  const getIntensity = (count) => {
    if (!count) return '#f1f5f9';
    const ratio = count / maxCount;
    if (ratio < 0.25) return '#bfdbfe';
    if (ratio < 0.5) return '#60a5fa';
    if (ratio < 0.75) return '#2563eb';
    return '#1e40af';
  };

  if (loading) return <Loading />;

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  return (
    <div className="page">
      <PageHeader title="Mapa de Calor" subtitle="Distribuição geográfica e temporal das operações" />

      {/* Filters */}
      <div className="filters-bar" style={{ marginBottom: '1.5rem' }}>
        {[['all', '📊 Todas'], ['entrega', '🚛 Entregas'], ['coleta', '📦 Coletas'], ['limpeza', '🧹 Limpezas']].map(([key, label]) => (
          <button key={key} className={`btn ${filter === key ? 'btn-primary' : 'btn-secondary'} btn-sm`}
            onClick={() => setFilter(key)}>{label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '.5rem' }}>
          {[['calendar', '📅'], ['chart', '📊']].map(([key, icon]) => (
            <button key={key} className={`btn ${view === key ? 'btn-primary' : 'btn-secondary'} btn-sm`}
              onClick={() => setView(key)}>{icon}
            </button>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total de Operações', value: filtered.length, color: '#1e40af', bg: '#dbeafe' },
          { label: 'Entregas', value: filtered.filter(o => o.logistics_type === 'entrega').length, color: '#16a34a', bg: '#dcfce7' },
          { label: 'Coletas', value: filtered.filter(o => o.logistics_type === 'coleta').length, color: '#d97706', bg: '#fef3c7' },
          { label: 'Limpezas', value: filtered.filter(o => o.logistics_type === 'limpeza').length, color: '#7c3aed', bg: '#ede9fe' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '1rem', display: 'flex', gap: '.75rem', alignItems: 'center' }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', fontWeight: 700, color: s.color }}>
              {s.value}
            </div>
            <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', lineHeight: 1.3 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Calendar heatmap */}
      {view === 'calendar' && (
        <div className="card" style={{ padding: '1.5rem' }}>
          <h3 style={{ margin: '0 0 1.25rem', fontSize: '1rem', fontWeight: 600 }}>
            Atividade nas Últimas 12 Semanas
          </h3>

          {/* Day labels */}
          <div style={{ display: 'flex', gap: '.25rem', marginBottom: '.25rem' }}>
            <div style={{ width: 32 }} />
            {weekDays.map(d => (
              <div key={d} style={{ flex: 1, textAlign: 'center', fontSize: '.7rem', color: 'var(--text-muted)', fontWeight: 600 }}>{d}</div>
            ))}
          </div>

          {/* Weeks */}
          {weeks.map((week, wi) => {
            const firstDate = week.find(d => d.date)?.date;
            const monthLabel = firstDate ? new Date(firstDate).toLocaleDateString('pt-BR', { month: 'short' }) : '';
            return (
              <div key={wi} style={{ display: 'flex', gap: '.25rem', marginBottom: '.25rem', alignItems: 'center' }}>
                <div style={{ width: 32, fontSize: '.65rem', color: 'var(--text-muted)', textAlign: 'right', paddingRight: 4 }}>
                  {wi % 4 === 0 ? monthLabel : ''}
                </div>
                {week.map(({ date, label, data }) => (
                  <div key={date} title={data ? `${label}: ${data.total} ops` : label}
                    style={{
                      flex: 1, aspectRatio: '1', borderRadius: 3,
                      background: getIntensity(data?.total || 0),
                      cursor: data ? 'pointer' : 'default',
                      transition: 'transform .1s',
                    }}
                    onMouseEnter={e => e.target.style.transform = 'scale(1.3)'}
                    onMouseLeave={e => e.target.style.transform = 'scale(1)'}
                  />
                ))}
              </div>
            );
          })}

          {/* Legend */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginTop: '1rem', fontSize: '.75rem', color: 'var(--text-muted)' }}>
            <span>Menos</span>
            {['#f1f5f9', '#bfdbfe', '#60a5fa', '#2563eb', '#1e40af'].map(c => (
              <div key={c} style={{ width: 14, height: 14, borderRadius: 2, background: c }} />
            ))}
            <span>Mais</span>
          </div>
        </div>
      )}

      {/* Bar chart */}
      {view === 'chart' && (
        <div className="card" style={{ padding: '1.5rem' }}>
          <h3 style={{ margin: '0 0 1.25rem', fontSize: '1rem', fontWeight: 600 }}>Operações por Mês</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '.75rem', height: 200 }}>
            {monthData.map(([month, count]) => {
              const maxVal = Math.max(...monthData.map(([, c]) => c), 1);
              const height = (count / maxVal) * 170;
              return (
                <div key={month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.25rem' }}>
                  <span style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-primary)' }}>{count}</span>
                  <div style={{ width: '100%', height, background: '#1e40af', borderRadius: '6px 6px 0 0', minHeight: 4 }} />
                  <span style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>
                    {new Date(month + '-01').toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })}
                  </span>
                </div>
              );
            })}
          </div>

          {/* By type breakdown */}
          <div style={{ marginTop: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
            {[
              { type: 'entrega', label: 'Entregas', color: '#22c55e' },
              { type: 'coleta', label: 'Coletas', color: '#f59e0b' },
              { type: 'limpeza', label: 'Limpezas', color: '#8b5cf6' },
            ].map(({ type, label, color }) => {
              const count = filtered.filter(o => o.logistics_type === type).length;
              const pct = filtered.length ? Math.round(count / filtered.length * 100) : 0;
              return (
                <div key={type} style={{ padding: '.75rem', background: 'var(--bg-secondary)', borderRadius: 8 }}>
                  <div style={{ fontWeight: 600, fontSize: '.875rem', marginBottom: '.5rem' }}>{label}</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color }}>{count}</div>
                  <div style={{ height: 4, background: '#e2e8f0', borderRadius: 2, marginTop: '.5rem' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 2 }} />
                  </div>
                  <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginTop: '.25rem' }}>{pct}% do total</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top addresses */}
      {filtered.length > 0 && (
        <div className="card" style={{ padding: '1.5rem', marginTop: '1rem' }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>Endereços Mais Atendidos</h3>
          {(() => {
            const addressCount = {};
            filtered.forEach(op => {
              const addr = op.destination_address || op.origin_address;
              if (addr) addressCount[addr] = (addressCount[addr] || 0) + 1;
            });
            return Object.entries(addressCount)
              .sort(([,a],[,b]) => b - a)
              .slice(0, 8)
              .map(([addr, count]) => (
                <div key={addr} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '.5rem .75rem', borderRadius: 6, background: 'var(--bg-secondary)', marginBottom: '.4rem', fontSize: '.85rem' }}>
                  <span style={{ color: 'var(--text-primary)' }}>📍 {addr}</span>
                  <span style={{ fontWeight: 700, color: '#1e40af', minWidth: 30, textAlign: 'right' }}>{count}x</span>
                </div>
              ));
          })()}
        </div>
      )}
    </div>
  );
}

// ── Audit Page ────────────────────────────────────────────────────────
export function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(0);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, offset: page * limit };
      if (entityFilter) params.entity_type = entityFilter;
      if (actionFilter) params.action = actionFilter;
      const [logsRes, summaryRes] = await Promise.all([
        auditService.list(params),
        page === 0 ? fetch('/api/audit/summary', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json()) : Promise.resolve(null),
      ]);
      const data = logsRes.data;
      setLogs(Array.isArray(data) ? data : data?.data ?? []);
      if (summaryRes) setSummary(summaryRes);
    } catch { toast.error('Erro ao carregar auditoria'); }
    finally { setLoading(false); }
  }, [entityFilter, actionFilter, page]);

  useEffect(() => { load(); }, [load]);

  const ENTITY_TYPES = ['Contract', 'Client', 'Asset', 'Logistics', 'Financial', 'Invoice', 'User', 'Maintenance'];
  const ACTIONS = ['CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'EMIT', 'SIGN', 'CHECKIN', 'CHECKOUT', 'PAY'];

  return (
    <div className="page">
      <PageHeader title="Auditoria" subtitle="Registro completo de todas as ações do sistema" />

      {/* Summary */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
          <div className="card" style={{ padding: '1.25rem' }}>
            <h4 style={{ margin: '0 0 .75rem', fontSize: '.85rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Por Ação</h4>
            {summary.by_action?.map(({ action, count }) => (
              <div key={action} style={{ display: 'flex', justifyContent: 'space-between', padding: '.3rem 0', fontSize: '.85rem', borderBottom: '1px solid var(--border)' }}>
                <span style={{ color: ACTION_COLORS[action] || 'var(--text-primary)' }}>
                  {ACTION_ICONS[action] || '•'} {action}
                </span>
                <strong>{count}</strong>
              </div>
            ))}
          </div>
          <div className="card" style={{ padding: '1.25rem' }}>
            <h4 style={{ margin: '0 0 .75rem', fontSize: '.85rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Por Usuário</h4>
            {summary.by_user?.map(({ user, count }) => (
              <div key={user} style={{ display: 'flex', justifyContent: 'space-between', padding: '.3rem 0', fontSize: '.85rem', borderBottom: '1px solid var(--border)' }}>
                <span>👤 {user || 'Sistema'}</span>
                <strong>{count}</strong>
              </div>
            ))}
          </div>
          <div className="card" style={{ padding: '1.25rem' }}>
            <h4 style={{ margin: '0 0 .75rem', fontSize: '.85rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Por Módulo</h4>
            {summary.by_entity?.map(({ entity, count }) => (
              <div key={entity} style={{ display: 'flex', justifyContent: 'space-between', padding: '.3rem 0', fontSize: '.85rem', borderBottom: '1px solid var(--border)' }}>
                <span>{entity || 'Geral'}</span>
                <strong>{count}</strong>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar" style={{ marginBottom: '1rem' }}>
        <select className="input" value={entityFilter} onChange={e => { setEntityFilter(e.target.value); setPage(0); }} style={{ width: 160 }}>
          <option value="">Todos os módulos</option>
          {ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="input" value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(0); }} style={{ width: 140 }}>
          <option value="">Todas as ações</option>
          {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <button className="btn btn-secondary btn-sm" onClick={() => { setEntityFilter(''); setActionFilter(''); setPage(0); }}>
          Limpar
        </button>
      </div>

      {/* Log table */}
      <div className="card">
        {loading ? <Loading /> : (
          <table className="table">
            <thead>
              <tr>
                <th>Data/Hora</th>
                <th>Ação</th>
                <th>Módulo</th>
                <th>Registro</th>
                <th>Usuário</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>Nenhum registro de auditoria ainda</td></tr>
              ) : logs.map(log => (
                <tr key={log.id}>
                  <td style={{ fontSize: '.8rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDate(log.created_at)}</td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.3rem', padding: '.2rem .6rem',
                      borderRadius: 20, fontSize: '.75rem', fontWeight: 600,
                      background: (ACTION_COLORS[log.action] || '#6b7280') + '20',
                      color: ACTION_COLORS[log.action] || '#6b7280' }}>
                      {ACTION_ICONS[log.action] || '•'} {log.action}
                    </span>
                  </td>
                  <td style={{ fontSize: '.85rem' }}>{log.entity_type || '—'}</td>
                  <td style={{ fontSize: '.85rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.entity_label || log.entity_id || '—'}
                  </td>
                  <td style={{ fontSize: '.85rem' }}>👤 {log.user_name || 'Sistema'}</td>
                  <td style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{log.ip_address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid var(--border)' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.max(0, p-1))} disabled={page === 0}>← Anterior</button>
          <span style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>Página {page + 1}</span>
          <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => p+1)} disabled={logs.length < limit}>Próxima →</button>
        </div>
      </div>
    </div>
  );
}
